import { memo, useContext, useState } from 'react';

import PageHeader from '@/components/PageHeader';
import ReleaseOfInformationList from '@/components/ReleaseOfInformationList';
import RequestedDocumentList from '@/components/RequestedDocumentList/RequestedDocumentList';
import RequestedFormCompletionList from '@/components/RequestedFormCompletionList';
import { Color } from '@/constants/color';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import useReleaseOfInformation from '@/hooks/useReleaseOfInformation';
import { useRequestedDocument } from '@/hooks/useRequestedDocument';
import { useRequestedFormCompletions } from '@/hooks/useRequestedFormCompletions';
import PersonContext from '@/pages/Person/PersonContext';
import PersonGeneratedDocuments from '@/pages/PersonGeneratedDocuments/PersonGeneratedDocuments';
import { getDocumentsByType } from '@/services/documentsService';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button';
import Container from '@/ui/components/Container';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';

enum DocumentsSegment {
  DOCUMENTS = 'documents',
  ROI = 'roi',
  REQUESTED_DOCUMENTS = 'requested_documents',
  GENERATED_DOCUMENTS = 'generated_documents'
}

const PersonRequestedFormCompletions = () => {
  const { person } = useContext(PersonContext);
  const [segment, setSegment] = useState<DocumentsSegment>(DocumentsSegment.DOCUMENTS);
  const { navigateToNewROI } = useNavigateToPage();

  const {
    data: requestedFormCompletions,
    isError: isDocumentsError,
    error: documentsError,
    isPending: isDocumentsPending
  } = useRequestedFormCompletions({
    ordering: '-created_at',
    person_id: person.id
  });

  const {
    data: releaseOfInformationList,
    isError: isReleaseOfInformationError,
    error: releaseOfInformationError,
    isPending: isReleaseOfInformationPending
  } = useReleaseOfInformation({
    person_id: person.id
  });

  const {
    data: requestedDocumentData,
    isError: isRequestedDocumentError,
    error: requestedDocumentError,
    isPending: isRequestedDocumentPending
  } = useRequestedDocument(person.id);

  const handleTabs = (newSegment: DocumentsSegment) => {
    setSegment(newSegment);
  };

  const activeButtonColor = Color.BLUE;

  const renderDocumentsTab = () => {
    if (isDocumentsPending) {
      return <LoadingSkeleton />;
    }

    if (isDocumentsError) {
      return <Alert level="error">{documentsError?.message}</Alert>;
    }

    if (!requestedFormCompletions?.length) {
      return <div>No requested form completions found.</div>;
    }

    // Show ALL documents without any filtering - ROI-related documents included naturally
    return (
      <div className="space-y-6">
        <RequestedFormCompletionList requestedFormCompletions={requestedFormCompletions} />
      </div>
    );
  };

  const renderRequestedDocumentsTab = () => {
    if (isRequestedDocumentPending) {
      return <LoadingSkeleton />;
    }

    if (isRequestedDocumentError) {
      return <Alert level="error">{requestedDocumentError?.message}</Alert>;
    }

    if (!requestedDocumentData?.data?.results?.length) {
      return <div>No requested documents found for this member.</div>;
    }

    return (
      <div className="space-y-6">
        <RequestedDocumentList requestedDocuments={requestedDocumentData.data.results} />
      </div>
    );
  };

  const renderRoiTab = () => {
    if (isReleaseOfInformationPending) {
      return <LoadingSkeleton />;
    }

    if (isReleaseOfInformationError) {
      return (
        <Alert level="error">
          {releaseOfInformationError?.message || 'Error loading Release of Information'}
        </Alert>
      );
    }

    const { roiRelatedDocuments } = getDocumentsByType(
      requestedFormCompletions || [],
      releaseOfInformationList?.data || []
    );

    return (
      <div className="space-y-6">
        <Button onClick={navigateToNewROI} className="mb-4" size="sm">
          New Release of Information
        </Button>
        {/* Formal Release of Information */}
        {releaseOfInformationList?.data?.length ? (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Release of Information Status
            </h3>
            <ReleaseOfInformationList releaseOfInformationList={releaseOfInformationList.data} />
          </div>
        ) : (
          <div>No Release of Information found for this member.</div>
        )}

        {/* ROI-Related Documents */}
        {roiRelatedDocuments.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Other ROI-Related Documents</h3>
            <Alert level="info" dismissable={false}>
              These are documents that contain &ldquo;Release&rdquo; or &ldquo;ROI&rdquo; in their
              title but are not formal Release of Information objects in our system.
            </Alert>
            <div className="mt-3">
              <RequestedFormCompletionList requestedFormCompletions={roiRelatedDocuments} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Container type="page">
      <PageHeader headingText="Documents" className="sr-only" />
      <div className="mb-2">
        <Alert level="info">This is the list of documents that the person sees in the app</Alert>
      </div>

      {/* Tab Navigation */}
      <div
        className={`border-b pb-1 border-gray-300 mb-4 w-full pt-1 z-19 bg-gray-100 ${STICKY_CHROME_CLASS.pageSection}`}>
        <div className="flex justify-start gap-2 mb-2">
          <Button
            className="capitalize"
            size="xs"
            color={segment === DocumentsSegment.DOCUMENTS ? activeButtonColor : Color.WHITE}
            onClick={() => handleTabs(DocumentsSegment.DOCUMENTS)}
            dataTestId="documents-tab">
            Documents
          </Button>
          <Button
            className="capitalize"
            size="xs"
            color={segment === DocumentsSegment.ROI ? activeButtonColor : Color.WHITE}
            onClick={() => handleTabs(DocumentsSegment.ROI)}
            dataTestId="roi-tab">
            Releases of Information
          </Button>
          <Button
            className="capitalize"
            size="xs"
            color={
              segment === DocumentsSegment.REQUESTED_DOCUMENTS ? activeButtonColor : Color.WHITE
            }
            onClick={() => handleTabs(DocumentsSegment.REQUESTED_DOCUMENTS)}
            dataTestId="requested-documents-tab">
            Requested Documents
          </Button>
          <Button
            className="capitalize"
            size="xs"
            color={
              segment === DocumentsSegment.GENERATED_DOCUMENTS ? activeButtonColor : Color.WHITE
            }
            onClick={() => handleTabs(DocumentsSegment.GENERATED_DOCUMENTS)}
            dataTestId="generated-documents-tab">
            Generated Documents
          </Button>
        </div>
      </div>

      {/* Tab Content */}
      {segment === DocumentsSegment.DOCUMENTS && renderDocumentsTab()}
      {segment === DocumentsSegment.ROI && renderRoiTab()}
      {segment === DocumentsSegment.REQUESTED_DOCUMENTS && renderRequestedDocumentsTab()}
      {segment === DocumentsSegment.GENERATED_DOCUMENTS && <PersonGeneratedDocuments />}
    </Container>
  );
};

export default memo(PersonRequestedFormCompletions);
